export type KarmaStats = {
  karmaScore: number
  offChainVotesPct: number
  onChainVotesPct: number
  forumActivityScore: number | null
}

type KarmaDelegate = {
  publicAddress: string
  stats: KarmaStats[]
}

type KarmaResponse = {
  responseTime: number
  data: {
    delegates: KarmaDelegate[]
  }
}

export type KarmaConfig = {
  period?: string
}
const DEFAULT_CONFIG = {
  period: "90d",
}

export const fetchKarmaScores = async (
  karmaName: string,
  addresses: string[],
  config: KarmaConfig = {},
): Promise<{ [address: string]: KarmaStats } | null> => {
  const _config = { ...DEFAULT_CONFIG, ...config }
  const baseUrl = "https://api.karmahq.xyz/api/dao/delegates"
  const addressesString = addresses.join(",")
  const requestUrl = `${baseUrl}?name=${karmaName}&pageSize=20&offset=0&period=${_config.period}&order=desc&field=score&statuses=active,inactive,withdrawn,recognized&addresses=${addressesString}`

  try {
    const response = await fetch(requestUrl)

    if (!response.ok) {
      throw new Error("Failed to fetch Karma scores")
    }

    const data: KarmaResponse = await response.json()
    const formattedData: { [address: string]: KarmaStats } = {}
    data.data.delegates.forEach((delegate) => {
      const stats = delegate.stats[0] || {
        karmaScore: 0,
        offChainVotesPct: 0,
        onChainVotesPct: 0,
        forumActivityScore: 0,
      }
      formattedData[delegate.publicAddress.toLowerCase()] = {
        // important to have it as lower case
        karmaScore: stats.karmaScore ? stats.karmaScore : 0,
        offChainVotesPct: stats.offChainVotesPct ? stats.offChainVotesPct : 0,
        onChainVotesPct: stats.onChainVotesPct ? stats.onChainVotesPct : 0,
        forumActivityScore: stats.forumActivityScore
          ? stats.forumActivityScore
          : 0,
      }
    })

    return formattedData
  } catch (_error) {
    return null
  }
}

export const fetchKarmaStats = async (
  karmaName?: string,
  address?: string,
  config: KarmaConfig = {},
): Promise<KarmaStats | null> => {
  if (!address || !karmaName) return null
  const _config = { ...DEFAULT_CONFIG, ...config }
  const baseUrl = "https://api.karmahq.xyz/api/dao/delegates"
  const requestUrl = `${baseUrl}?name=${karmaName}&pageSize=20&offset=0&period=${
    _config.period
  }&order=desc&field=score&statuses=active,inactive,withdrawn,recognized&addresses=${address.toLowerCase()}`

  try {
    const response = await fetch(requestUrl)

    if (!response.ok) {
      throw new Error("Failed to fetch Karma scores")
    }

    const data: KarmaResponse = await response.json()

    const emptyState = {
      karmaScore: 0,
      offChainVotesPct: 0,
      onChainVotesPct: 0,
      forumActivityScore: 0,
    }

    if (data.data.delegates.length === 0) {
      return emptyState
    }

    const userData = data.data.delegates.find(
      (delegate) =>
        delegate?.publicAddress.toLowerCase() === address.toLowerCase(),
    )

    if (!userData) return emptyState

    return {
      karmaScore: userData.stats[0].karmaScore,
      offChainVotesPct: userData.stats[0].offChainVotesPct,
      onChainVotesPct: userData.stats[0].onChainVotesPct,
      forumActivityScore: userData.stats[0].forumActivityScore,
    }
  } catch (error) {
    console.error("Failed to fetch Karma scores", error)

    return null
  }
}
